import React, { useState, useEffect, useMemo } from 'react';
import {
  ModalProps,
  ModalContextType,
  ModalButton,
  ModalBadge,
  ModalIcon,
  ModalTabsSwitchProps,
} from '@/shared/types/modal';
import { BUTTON_SLOT_SPREAD_CENTER } from '@/shared/types/button';
import { warnDeprecatedPropList } from '@/modules/InnerButton/utils/deprecation';
import { ModalContext } from './ModalContext.context';

const MODAL_TAG = '[Modal]';

/**
 * Convierte las props específicas de botones del footer a array
 * Procesa footerPrimaryButton (success) y footerSecondaryButton (cancel)
 */
const processFooterButtons = (primary?: ModalButton, secondary?: ModalButton): ModalButton[] => {
  const buttons: ModalButton[] = [];

  // Secondary = Cancel (se muestra primero a la izquierda)
  if (secondary && secondary.show !== false) {
    buttons.push({ ...secondary, id: secondary.id || 'footer-secondary' });
  }
  // Primary = Success (se muestra segundo a la derecha)
  if (primary && primary.show !== false) {
    buttons.push({ ...primary, id: primary.id || 'footer-primary' });
  }

  return buttons;
};

/**
 * Convierte las props legacy del icono principal a ModalIcon
 */
const convertLegacyIconToIconConfig = (
  props: Pick<
    ModalProps,
    | 'icon'
    | 'iconDuotonePrimary'
    | 'iconDuotoneSecondary'
    | 'iconDuotoneOpacityPrimary'
    | 'iconDuotoneOpacitySecondary'
  >,
): ModalIcon | undefined => {
  if (!props.icon) return undefined;

  const deprecatedUsed: string[] = ['icon'];
  if (props.iconDuotonePrimary) deprecatedUsed.push('iconDuotonePrimary');
  if (props.iconDuotoneSecondary) deprecatedUsed.push('iconDuotoneSecondary');
  if (props.iconDuotoneOpacityPrimary !== undefined) {
    deprecatedUsed.push('iconDuotoneOpacityPrimary');
  }
  if (props.iconDuotoneOpacitySecondary !== undefined) {
    deprecatedUsed.push('iconDuotoneOpacitySecondary');
  }
  warnDeprecatedPropList(deprecatedUsed, 'iconConfig', MODAL_TAG);

  return {
    icon: props.icon,
    show: true,
    iconDuotonePrimary: props.iconDuotonePrimary,
    iconDuotoneSecondary: props.iconDuotoneSecondary,
    iconDuotoneOpacityPrimary: props.iconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: props.iconDuotoneOpacitySecondary,
  };
};

/**
 * Convierte las props legacy del header button a ModalButton
 */
const convertLegacyHeaderButtonToButton = (
  props: Pick<
    ModalProps,
    | 'headerButtonIcon'
    | 'headerButtonIconDuotonePrimary'
    | 'headerButtonIconDuotoneSecondary'
    | 'headerButtonIconDuotoneOpacityPrimary'
    | 'headerButtonIconDuotoneOpacitySecondary'
    | 'headerButtonOnClick'
  >,
): ModalButton | undefined => {
  if (!props.headerButtonOnClick && !props.headerButtonIcon) return undefined;

  const deprecatedUsed: string[] = [];
  if (props.headerButtonIcon) deprecatedUsed.push('headerButtonIcon');
  if (props.headerButtonOnClick) deprecatedUsed.push('headerButtonOnClick');
  if (props.headerButtonIconDuotonePrimary) {
    deprecatedUsed.push('headerButtonIconDuotonePrimary');
  }
  if (props.headerButtonIconDuotoneSecondary) {
    deprecatedUsed.push('headerButtonIconDuotoneSecondary');
  }
  if (props.headerButtonIconDuotoneOpacityPrimary !== undefined) {
    deprecatedUsed.push('headerButtonIconDuotoneOpacityPrimary');
  }
  if (props.headerButtonIconDuotoneOpacitySecondary !== undefined) {
    deprecatedUsed.push('headerButtonIconDuotoneOpacitySecondary');
  }
  warnDeprecatedPropList(deprecatedUsed, 'headerButton', MODAL_TAG);

  return {
    id: 'header-button',
    icon: props.headerButtonIcon,
    onClick: props.headerButtonOnClick,
    theme: 'solid',
    color: 'secondary',
    size: 'xs',
    pill: 'pill',
    show: true,
    iconDuotonePrimary: props.headerButtonIconDuotonePrimary,
    iconDuotoneSecondary: props.headerButtonIconDuotoneSecondary,
    iconDuotoneOpacityPrimary: props.headerButtonIconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: props.headerButtonIconDuotoneOpacitySecondary,
  };
};

/**
 * Convierte las props legacy del badge a ModalBadge
 */
const convertLegacyBadgeToBadge = (
  props: Pick<
    ModalProps,
    | 'badgeIcon'
    | 'badgeIconDuotonePrimary'
    | 'badgeIconDuotoneSecondary'
    | 'badgeIconDuotoneOpacityPrimary'
    | 'badgeIconDuotoneOpacitySecondary'
    | 'badgeLabel'
  >,
): ModalBadge | undefined => {
  if (!props.badgeLabel) return undefined;

  const deprecatedUsed: string[] = ['badgeLabel'];
  if (props.badgeIcon) deprecatedUsed.push('badgeIcon');
  if (props.badgeIconDuotonePrimary) deprecatedUsed.push('badgeIconDuotonePrimary');
  if (props.badgeIconDuotoneSecondary) deprecatedUsed.push('badgeIconDuotoneSecondary');
  if (props.badgeIconDuotoneOpacityPrimary !== undefined) {
    deprecatedUsed.push('badgeIconDuotoneOpacityPrimary');
  }
  if (props.badgeIconDuotoneOpacitySecondary !== undefined) {
    deprecatedUsed.push('badgeIconDuotoneOpacitySecondary');
  }
  warnDeprecatedPropList(deprecatedUsed, 'badge', MODAL_TAG);

  return {
    id: 'badge',
    label: props.badgeLabel,
    icon: props.badgeIcon,
    color: 'accent',
    size: 'sm',
    show: true,
    iconDuotonePrimary: props.badgeIconDuotonePrimary,
    iconDuotoneSecondary: props.badgeIconDuotoneSecondary,
    iconDuotoneOpacityPrimary: props.badgeIconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: props.badgeIconDuotoneOpacitySecondary,
  };
};

/**
 * Convierte las props legacy del footer aux button a ModalButton
 */
const convertLegacyFooterAuxButtonToButton = (
  props: Pick<
    ModalProps,
    | 'footerButtonIcon'
    | 'footerButtonIconDuotonePrimary'
    | 'footerButtonIconDuotoneSecondary'
    | 'footerButtonIconDuotoneOpacityPrimary'
    | 'footerButtonIconDuotoneOpacitySecondary'
    | 'footerButtonOnClick'
  >,
): ModalButton | undefined => {
  if (!props.footerButtonOnClick && !props.footerButtonIcon) return undefined;

  const deprecatedUsed: string[] = [];
  if (props.footerButtonIcon) deprecatedUsed.push('footerButtonIcon');
  if (props.footerButtonOnClick) deprecatedUsed.push('footerButtonOnClick');
  if (props.footerButtonIconDuotonePrimary) {
    deprecatedUsed.push('footerButtonIconDuotonePrimary');
  }
  if (props.footerButtonIconDuotoneSecondary) {
    deprecatedUsed.push('footerButtonIconDuotoneSecondary');
  }
  if (props.footerButtonIconDuotoneOpacityPrimary !== undefined) {
    deprecatedUsed.push('footerButtonIconDuotoneOpacityPrimary');
  }
  if (props.footerButtonIconDuotoneOpacitySecondary !== undefined) {
    deprecatedUsed.push('footerButtonIconDuotoneOpacitySecondary');
  }
  warnDeprecatedPropList(deprecatedUsed, 'footerAuxButton', MODAL_TAG);

  return {
    id: 'footer-aux-button',
    icon: props.footerButtonIcon,
    onClick: props.footerButtonOnClick,
    theme: 'solid',
    color: 'secondary',
    size: 'xs',
    pill: 'pill',
    show: true,
    iconDuotonePrimary: props.footerButtonIconDuotonePrimary,
    iconDuotoneSecondary: props.footerButtonIconDuotoneSecondary,
    iconDuotoneOpacityPrimary: props.footerButtonIconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: props.footerButtonIconDuotoneOpacitySecondary,
  };
};

/**
 * Convierte las props legacy del Modal a el nuevo formato de footerButtons
 * Mantiene retrocompatibilidad con el sistema anterior
 */
const convertLegacyPropsToFooterButtons = (
  props: Pick<
    ModalProps,
    | 'cancelButtonText'
    | 'successButtonText'
    | 'onCancel'
    | 'onSuccess'
    | 'showCancelButton'
    | 'showSuccessButton'
    | 'closeAtCancel'
    | 'closeAtSuccess'
    | 'cancelButtonSlotLeft'
    | 'successButtonSlotRight'
    | 'disableCancelButton'
    | 'disableSuccessButton'
    | 'successButtonColor'
    | 'footerSlotLayout'
  >,
): ModalButton[] => {
  if (props.showCancelButton || props.showSuccessButton) {
    const deprecatedUsed: string[] = [];
    if (props.showCancelButton) deprecatedUsed.push('showCancelButton');
    if (props.showSuccessButton) deprecatedUsed.push('showSuccessButton');
    if (props.cancelButtonText) deprecatedUsed.push('cancelButtonText');
    if (props.successButtonText) deprecatedUsed.push('successButtonText');
    if (props.onCancel) deprecatedUsed.push('onCancel');
    if (props.onSuccess) deprecatedUsed.push('onSuccess');
    if (props.cancelButtonSlotLeft) deprecatedUsed.push('cancelButtonSlotLeft');
    if (props.successButtonSlotRight) deprecatedUsed.push('successButtonSlotRight');
    if (props.disableCancelButton) deprecatedUsed.push('disableCancelButton');
    if (props.disableSuccessButton) deprecatedUsed.push('disableSuccessButton');
    if (props.successButtonColor) deprecatedUsed.push('successButtonColor');
    if (props.footerSlotLayout) deprecatedUsed.push('footerSlotLayout');
    warnDeprecatedPropList(
      deprecatedUsed,
      'footerPrimaryButton (success) / footerSecondaryButton (cancel)',
      MODAL_TAG,
    );
  }

  const buttons: ModalButton[] = [];

  const spreadFooter =
    props.footerSlotLayout === 'spread'
      ? {
        slotLayout: BUTTON_SLOT_SPREAD_CENTER,
        fullWidth: true,
        className: 'ui-flex-1 ui-min-w-0',
      }
      : {};

  // Botón de cancelar (si está habilitado)
  if (props.showCancelButton) {
    buttons.push({
      id: 'cancel',
      label: props.cancelButtonText || 'Cancelar',
      onClick: props.onCancel,
      color: 'secondary',
      disabled: props.disableCancelButton,
      leftSlot: props.cancelButtonSlotLeft,
      closeOnClick: props.closeAtCancel ?? true,
      show: true,
      theme: 'solid',
      size: 'xs',
      pill: 'sm',
      ...spreadFooter,
    });
  }

  // Botón de éxito (si está habilitado)
  if (props.showSuccessButton) {
    buttons.push({
      id: 'success',
      label: props.successButtonText || 'Guardar',
      onClick: props.onSuccess,
      color: props.successButtonColor || 'primary',
      disabled: props.disableSuccessButton,
      rightSlot: props.successButtonSlotRight,
      closeOnClick: props.closeAtSuccess ?? false,
      show: true,
      theme: 'solid',
      size: 'xs',
      pill: 'sm',
      ...spreadFooter,
    });
  }

  return buttons;
};

export const ModalProvider: React.FC<ModalProps> = ({
  children,
  onClose,
  // Nuevo sistema (modelo cerrado)
  iconConfig,
  closeButton,
  headerButton,
  headerButtons,
  badge,
  badges,
  footerAuxButton,
  footerPrimaryButton,
  footerSecondaryButton,
  footerButtons,
  tabsConfig,
  // Props legacy
  onSuccess,
  icon,
  iconDuotonePrimary,
  iconDuotoneSecondary,
  iconDuotoneOpacityPrimary,
  iconDuotoneOpacitySecondary,
  closeAtSuccess = false,
  successButtonText = 'Guardar',
  showSuccessButton = true,
  onCancel,
  cancelButtonText = 'Cancelar',
  closeAtCancel = true,
  showCancelButton = true,
  showCloseButton = true,
  closeAtCorner = true,
  title,
  tabs,
  defaultValueTab,
  onChangeTab,
  headerButtonIcon,
  headerButtonIconDuotonePrimary,
  headerButtonIconDuotoneSecondary,
  headerButtonIconDuotoneOpacityPrimary,
  headerButtonIconDuotoneOpacitySecondary,
  headerButtonOnClick,
  footerButtonIcon,
  footerButtonIconDuotonePrimary,
  footerButtonIconDuotoneSecondary,
  footerButtonIconDuotoneOpacityPrimary,
  footerButtonIconDuotoneOpacitySecondary,
  footerButtonOnClick,
  cancelButtonSlotLeft,
  successButtonSlotRight,
  disableCancelButton,
  disableSuccessButton,
  successButtonColor = 'primary',
  footerSlotLayout = 'inline',
  badgeIcon,
  badgeIconDuotonePrimary,
  badgeIconDuotoneSecondary,
  badgeIconDuotoneOpacityPrimary,
  badgeIconDuotoneOpacitySecondary,
  badgeLabel,
  hideFooter = false,
  sectionPadding = 'md',
}) => {
  const { resolvedTabOptions } = useMemo(() => {
    const fromConfig =
      tabsConfig?.options && tabsConfig.options.length > 0 ? tabsConfig.options : null;
    const fromLegacy = tabs && tabs.length > 0 ? tabs : null;

    if (tabsConfig) {
      if (fromLegacy) {
        warnDeprecatedPropList(
          ['tabs'],
          'tabsConfig.options (solo se aplica tabsConfig)',
          MODAL_TAG,
        );
      }
      if (defaultValueTab !== undefined) {
        warnDeprecatedPropList(['defaultValueTab'], 'tabsConfig.defaultValueTab', MODAL_TAG);
      }
      if (onChangeTab !== undefined) {
        warnDeprecatedPropList(['onChangeTab'], 'tabsConfig.onChangeTab', MODAL_TAG);
      }
    } else {
      const deprecatedRoot: string[] = [];
      if (fromLegacy) deprecatedRoot.push('tabs');
      if (defaultValueTab !== undefined) deprecatedRoot.push('defaultValueTab');
      if (onChangeTab !== undefined) deprecatedRoot.push('onChangeTab');
      if (deprecatedRoot.length > 0) {
        warnDeprecatedPropList(deprecatedRoot, 'tabsConfig', MODAL_TAG);
      }
    }

    return { resolvedTabOptions: fromConfig ?? fromLegacy ?? [] };
  }, [tabsConfig, tabs, defaultValueTab, onChangeTab]);

  const tabsSwitchProps = useMemo<ModalTabsSwitchProps>(
    () => ({
      size: 'sm',
      colorIcon: 'primary',
      fullWidth: false,
      ...tabsConfig?.switch,
    }),
    [tabsConfig?.switch],
  );

  const effectiveDefaultValueTab = tabsConfig?.defaultValueTab ?? defaultValueTab;

  const [currentTab, setCurrentTab] = useState<string | undefined>(effectiveDefaultValueTab);

  useEffect(() => {
    if (effectiveDefaultValueTab !== undefined) {
      setCurrentTab(effectiveDefaultValueTab);
    }
  }, [effectiveDefaultValueTab]);

  const handleChangeTab = (value: string) => {
    setCurrentTab(value);
    const userHandler = tabsConfig?.onChangeTab ?? onChangeTab;
    userHandler?.(value);
  };

  // Procesar el icono principal
  const processedIcon = useMemo<ModalIcon | undefined>(() => {
    if (iconConfig) {
      return iconConfig.show !== false ? iconConfig : undefined;
    }
    return convertLegacyIconToIconConfig({
      icon,
      iconDuotonePrimary,
      iconDuotoneSecondary,
      iconDuotoneOpacityPrimary,
      iconDuotoneOpacitySecondary,
    });
  }, [
    iconConfig,
    icon,
    iconDuotonePrimary,
    iconDuotoneSecondary,
    iconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary,
  ]);

  // Procesar los botones del header
  const processedHeaderButtons = useMemo<ModalButton[]>(() => {
    // Prioridad: headerButtons (array) > headerButton (singular) > props legacy
    if (headerButtons && headerButtons.length > 0) {
      return headerButtons.filter((btn) => btn.show !== false);
    }

    if (headerButton && headerButton.show !== false) {
      return [{ ...headerButton, id: headerButton.id || 'header-button' }];
    }

    // Si no, convertir las props legacy
    const legacyButton = convertLegacyHeaderButtonToButton({
      headerButtonIcon,
      headerButtonIconDuotonePrimary,
      headerButtonIconDuotoneSecondary,
      headerButtonIconDuotoneOpacityPrimary,
      headerButtonIconDuotoneOpacitySecondary,
      headerButtonOnClick,
    });
    return legacyButton ? [legacyButton] : [];
  }, [
    headerButtons,
    headerButton,
    headerButtonIcon,
    headerButtonIconDuotonePrimary,
    headerButtonIconDuotoneSecondary,
    headerButtonIconDuotoneOpacityPrimary,
    headerButtonIconDuotoneOpacitySecondary,
    headerButtonOnClick,
  ]);

  // Procesar los badges
  const processedBadges = useMemo<ModalBadge[]>(() => {
    // Prioridad: badges (array) > badge (singular) > props legacy
    if (badges && badges.length > 0) {
      return badges.filter((badge) => badge.show !== false);
    }

    if (badge && badge.show !== false) {
      return [{ ...badge, id: badge.id || 'badge' }];
    }

    // Si no, convertir las props legacy
    const legacyBadge = convertLegacyBadgeToBadge({
      badgeIcon,
      badgeIconDuotonePrimary,
      badgeIconDuotoneSecondary,
      badgeIconDuotoneOpacityPrimary,
      badgeIconDuotoneOpacitySecondary,
      badgeLabel,
    });
    return legacyBadge ? [legacyBadge] : [];
  }, [
    badges,
    badge,
    badgeIcon,
    badgeIconDuotonePrimary,
    badgeIconDuotoneSecondary,
    badgeIconDuotoneOpacityPrimary,
    badgeIconDuotoneOpacitySecondary,
    badgeLabel,
  ]);

  // Procesar el botón auxiliar del footer
  const processedFooterAuxButton = useMemo<ModalButton | undefined>(() => {
    if (footerAuxButton) {
      return footerAuxButton.show !== false ? footerAuxButton : undefined;
    }
    return convertLegacyFooterAuxButtonToButton({
      footerButtonIcon,
      footerButtonIconDuotonePrimary,
      footerButtonIconDuotoneSecondary,
      footerButtonIconDuotoneOpacityPrimary,
      footerButtonIconDuotoneOpacitySecondary,
      footerButtonOnClick,
    });
  }, [
    footerAuxButton,
    footerButtonIcon,
    footerButtonIconDuotonePrimary,
    footerButtonIconDuotoneSecondary,
    footerButtonIconDuotoneOpacityPrimary,
    footerButtonIconDuotoneOpacitySecondary,
    footerButtonOnClick,
  ]);

  // Procesar los botones del footer
  const processedFooterButtons = useMemo<ModalButton[]>(() => {
    // Prioridad: footerButtons (array) > footerPrimaryButton/footerSecondaryButton > props legacy
    if (footerButtons && footerButtons.length > 0) {
      return footerButtons.filter((btn) => btn.show !== false);
    }

    const newButtons = processFooterButtons(footerPrimaryButton, footerSecondaryButton);
    if (newButtons.length > 0) {
      return newButtons;
    }

    // Si no, convertir las props legacy
    return convertLegacyPropsToFooterButtons({
      cancelButtonText,
      successButtonText,
      onCancel,
      onSuccess,
      showCancelButton,
      showSuccessButton,
      closeAtCancel,
      closeAtSuccess,
      cancelButtonSlotLeft,
      successButtonSlotRight,
      disableCancelButton,
      disableSuccessButton,
      successButtonColor,
      footerSlotLayout,
    });
  }, [
    footerButtons,
    footerPrimaryButton,
    footerSecondaryButton,
    cancelButtonText,
    successButtonText,
    onCancel,
    onSuccess,
    showCancelButton,
    showSuccessButton,
    closeAtCancel,
    closeAtSuccess,
    cancelButtonSlotLeft,
    successButtonSlotRight,
    disableCancelButton,
    disableSuccessButton,
    successButtonColor,
    footerSlotLayout,
  ]);

  const contextValue: ModalContextType = {
    // Nuevo sistema (modelo cerrado)
    iconConfig,
    closeButton,
    headerButton,
    headerButtons,
    badge,
    badges,
    footerAuxButton,
    footerPrimaryButton,
    footerSecondaryButton,
    footerButtons,
    // Procesados
    processedIcon,
    processedHeaderButtons,
    processedBadges,
    processedFooterAuxButton,
    processedFooterButtons,
    // Props legacy (mantener para retrocompatibilidad)
    onClose,
    showCloseButton,
    closeAtCorner,
    cancelButtonText,
    successButtonText,
    onCancel,
    onSuccess,
    showCancelButton,
    showSuccessButton,
    closeAtSuccess,
    closeAtCancel,
    title,
    icon,
    iconDuotonePrimary,
    iconDuotoneSecondary,
    iconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary,
    tabs: resolvedTabOptions,
    tabsConfig,
    defaultValueTab,
    onChangeTab: handleChangeTab,
    currentTab,
    tabsSwitchProps,
    headerButtonIcon,
    headerButtonIconDuotonePrimary,
    headerButtonIconDuotoneSecondary,
    headerButtonIconDuotoneOpacityPrimary,
    headerButtonIconDuotoneOpacitySecondary,
    headerButtonOnClick,
    footerButtonIcon,
    footerButtonIconDuotonePrimary,
    footerButtonIconDuotoneSecondary,
    footerButtonIconDuotoneOpacityPrimary,
    footerButtonIconDuotoneOpacitySecondary,
    footerButtonOnClick,
    cancelButtonSlotLeft,
    successButtonSlotRight,
    disableCancelButton,
    disableSuccessButton,
    successButtonColor,
    footerSlotLayout,
    badgeIcon,
    badgeIconDuotonePrimary,
    badgeIconDuotoneSecondary,
    badgeIconDuotoneOpacityPrimary,
    badgeIconDuotoneOpacitySecondary,
    badgeLabel,
    hideFooter,
    sectionPadding,
  };

  return <ModalContext.Provider value={contextValue}>{children}</ModalContext.Provider>;
};

export type { ModalContextType };
